import { action, computed, observable, reaction, makeObservable } from 'mobx';
import {
    Control,
    Disposers,
    HttpPromiseState,
    StringControl,
    validators
} from '../../../../lib/helpers';
import { PagedListResponse, pagedListResponseUtils } from '../../../../domain';
import {
    Classroom,
    ClassroomSearchDto,
    ClassroomStatus,
    ClassroomUserFilter
} from '../../../classroom/models/classroom-models';
import { ClassroomLoaders } from '../../../classroom/stores/classroom-loaders';

const U = pagedListResponseUtils;

export class ClassroomSearchStore {
    @observable
    expanded: boolean;

    @observable
    currentPage: number;

    @observable.ref
    studentFilter: ClassroomUserFilter;

    @observable.ref
    teacherFilter: ClassroomUserFilter;

    startDateFilter: StringControl;

    readonly endDateFilter: StringControl = new StringControl('', '', validators.dateRange);

    statusFilter: Control<ClassroomStatus | null>;

    @observable.ref
    resultState: HttpPromiseState<PagedListResponse<Classroom>> | null;

    private readonly disposers = new Disposers();

    constructor() {
        makeObservable(this);
        this.expanded = true;
        this.currentPage = 0;
        this.studentFilter = this.teacherFilter = {};
        this.startDateFilter = new StringControl('', '', validators.dateRange);
        this.statusFilter = new Control<ClassroomStatus | null>('', null);
        this.resultState = null;
        this.setup();
    }

    @action
    toggleExpand = () => {
        this.expanded = !this.expanded;
    };

    @action
    updateStudentFilter(filter: ClassroomUserFilter) {
        if (filter.Filter === this.studentFilter.Filter && filter.UserId === this.studentFilter.Filter)
            return;
        this.studentFilter = filter;
    }

    @action
    updateTeacherFilter(filter: ClassroomUserFilter) {
        if (filter.Filter === this.teacherFilter.Filter && filter.UserId === this.teacherFilter.Filter)
            return;
        this.teacherFilter = filter;
    }

    @action
    searchClassrooms = () => {
        if (this.resultState?.isPending === true)
            return;
        this.expanded = true;
        const model: ClassroomSearchDto = {
            Page: this.currentPage,
            Student: this.studentFilter,
            Teacher: this.teacherFilter,
            StartDate: this.startDateFilter.value,
            Status: this.statusFilter.value,
            EndDate: this.endDateFilter.value,
        };
        this.resultState = ClassroomLoaders.Admin.searchClassroom(model);
    };

    @computed
    get hasPreviousPage(): boolean {
        if (this.resultState?.isResolved !== true)
            return false;
        return U.hasPreviousPage(this.resultState.value);
    }

    @action
    loadPreviousPage = () => {
        this.currentPage--;
        this.searchClassrooms();
    };

    @computed
    get hasNextPage(): boolean {
        if (this.resultState?.isResolved !== true)
            return false;
        return U.hasNextPage(this.resultState.value);
    }

    @action
    loadNextPage = () => {
        this.currentPage++;
        this.searchClassrooms();
    };

    private setup() {
        this.disposers.add(
            reaction(
                () => [this.studentFilter, this.teacherFilter,
                this.startDateFilter.value, this.statusFilter.value],
                this.resetResultsOnFilterChange),
            reaction(
                () => [this.resultState?.value],
                this.updateCurrentPage
            ));
    }

    @action
    private resetResultsOnFilterChange = () => {
        this.currentPage = 0;
        this.resultState = null;
    };

    @action
    private updateCurrentPage = () => {
        if (this.resultState?.isResolved !== true)
            return;
        this.expanded = true;
        this.currentPage = this.resultState.value.CurrentPage;
    };

    dispose = () => {
        this.disposers.dispose();
    };
}
